import React, {useLayoutEffect, useEffect, useRef} from 'react'; // eslint-disable-line no-unused-vars

import {initialize, pageView} from 'polyphony/gtags';
import {useSelector, useForceUpdate, useListener, useJavaScript} from 'polyphony/hooks';
import {isFirefox, isIE, isSafari} from 'polyphony/browser';

import {withNoProps} from 'polyphony/hoc';
import {executeAfter} from 'polyphony/util';
import {splitURL, scroll, removeNode } from 'polyphony/dom';
import {htmlToFragment} from 'polyphony/html';
import {getAttachment} from 'polyphony/networking';

import '@fancyapps/fancybox';
import '@fancyapps/fancybox/dist/jquery.fancybox.min.css';

/*
    This component uses vanilla JS to apply changes to the DOM
    that cannot be accomplished through React.

    It also does all the hacky shit WordPress forces us to do.
*/

const useCurrentPost = () => useSelector(({is, posts, overrode_post}) => {
    if (is && is.singular && posts[0]) {
        return posts[0];
    } else if (overrode_post) {
        return overrode_post;
    }
    return null;
}, (a, b) => (b ? b.ID : null) === (a ? a.ID : null));

const useSiteDescription = () => {
    const siteDescription = useSelector(s => s.site_description);
    const post = useCurrentPost();
    if (post) {
        if (post.excerpt && post.excerpt.length > 0) return post.excerpt;
    }
    return siteDescription;
};

const selector = ({progressive_web_app, body_classes, is, posts, favicon, overrode_post, watch_settings, google_tag_manager}) => {
    let post_id = null;
    let editText = null;
    if (is && is.singular && posts[0]) {
        post_id = posts[0].ID;
        editText = posts[0].post_type.labels.edit_item;
    } else if (overrode_post) {
        post_id = overrode_post.ID;
        editText = overrode_post.post_type.labels.edit_item;
    }
    return {
        body_classes,
        pwa: progressive_web_app,
        post_id,
        editText,
        faviconHref: favicon && favicon.favicon ? favicon.favicon.url : null,
        faviconMime: favicon && favicon.favicon ? favicon.favicon.mimeType : null,
        watch_settings,
        google_tag_manager
    };
};

export const Chrome = withNoProps(() => { // esline-disable-line no-unused-vars
    const [slug, forceUpdate] = useForceUpdate();
    const focusMode = useSelector(s => s.focusMode);
    const loading = useSelector(s => s.loading);
    const loadingReplacement = useSelector(s => s.loadingReplacement);
    const notFound = useSelector(s => s.is && s.is['404'] ? true : false);

    const currentPost = useCurrentPost();
    const post_id = currentPost ? currentPost.ID : null;
    const editText = currentPost ? currentPost.post_type.labels.edit_item : null;

    const site_description = useSiteDescription();

    const backgroundColor = useSelector(s => s.colors.body_background);
    const site_name = useSelector(s => s.site_name);
    const webpage_title = useSelector(s => s.webpage_title);
    const hashScrollOffset = useSelector(s => s.hashScrollOffset);
    const { pwa, body_classes, faviconHref, faviconMime, watch_settings, google_tag_manager } = useSelector(selector);

    const bodyJS = useSelector(s => s.code.body_js);
    const headJS = useSelector(s => s.code.head_js);

    //
    // Code
    //

    useJavaScript(document.body, currentPost ? currentPost.meta.polyphony_inline_javascript : null);
    useJavaScript(document.body, bodyJS);
    useJavaScript(document.head, headJS);

    //
    // Google Tag Manager
    //

    // TODO: render metadata for everyone except browsers (based on user agent) - as opposed to major search engines only.

    const {analytics_enabled: gaEnabled, analytics_tracking_id: gaID} = google_tag_manager;
    useLayoutEffect(() => {
        return gaEnabled && gaID ? initialize(gaID) : () => {};
    }, [gaEnabled, gaID]);

    // TODO: Fix how the page title only ever gets reported
    //       as the blog title (so the | and beyond is skipped)
    const {rootRelative: currentURLPath} = splitURL();
    useLayoutEffect(() => {
        if (gaEnabled && gaID && currentURLPath) {
            pageView({title: webpage_title,
                      path: currentURLPath,
                      loc: window.location.href});
        }
    }, [currentURLPath]);

    //
    // Update focus mode: tab or mouse
    //

    useListener(window, 'mousemove', focusMode === 'mouse' ? () => {} : () => {
        polyphony.setFocusMode('mouse');
    }, [focusMode], {passive: true});

    useListener(window, 'keydown', focusMode === 'tab' ? () => {} : e => {
        if (e.key === 'Tab') {
            polyphony.setFocusMode('tab');
        }
    }, [focusMode], {passive: true});

    useLayoutEffect(() => {
        if (isFirefox()) {
            document.documentElement.classList.add('is-firefox');
        } else if (isSafari()) {
            document.documentElement.classList.add('is-safari');
        } else if (isIE()) {
            document.documentElement.classList.add('is-ie');
        }
    });

    useLayoutEffect(() => {
        if (focusMode === 'mouse') {
            document.documentElement.classList.add('polyphony-mouse-focus');
            document.documentElement.classList.remove('polyphony-tab-focus');
        } else if (focusMode === 'tab') {
            document.documentElement.classList.add('polyphony-tab-focus');
            document.documentElement.classList.remove('polyphony-mouse-focus');
        }
    }, [focusMode]);

    //
    // Patch <a></a> tags controlled by React
    //
    // If an href starts with out origin or is relative,
    // run it through our query endpoint instead of navigating to it.
    //

    const spaSyntheticHref = (href, behavior) => {
        const {rootRelative, origin, hash} = splitURL(href);

        // If the URL is on our site and its path isn't a WordPress admin page,
        // run it through the query endpoint.
        if (origin === window.location.origin && !rootRelative.startsWith('/wp-')) {

            // Now we check to make sure the link has valid behavior
            const type = behavior || 'normal';
            if (['normal', 'replace', 'load_more'].includes(type)) {
                const {rootRelative: oldRootRelative, hash: oldHash} = splitURL();

                const relativeChanged = oldRootRelative !== rootRelative;
                const hashChanged = oldHash !== hash;

                // Update the URL and browser history
                if (relativeChanged || hashChanged) {
                    window.history.pushState(null, null, rootRelative + (hash ? '#' + hash : ''));
                }

                // Avoid double querying when a URL hash is involved.
                if (relativeChanged) {
                    polyphony.loadContent(rootRelative, {}, type === 'replace');
                }

                forceUpdate();
                return true;
            }
        }
        return false;
    };

    const onLinkClick = e => {
        const path = e.path ? e.path : e.composedPath ? e.composedPath() : [];
        const el = path.find(el => el.tagName === 'A' && !!el.href);
        if (el && el.tagName === 'A' && el.href) {
            if (spaSyntheticHref(el.href, el.dataset.behavior)) {
                e.preventDefault();
            }
        }
    };

    useListener(window, 'click', onLinkClick);
    useListener(window, 'popstate', () => polyphony.loadContent());

    /* Update the background color */
    useLayoutEffect(() => {
        document.body.style.backgroundColor = backgroundColor;
    }, [backgroundColor]);

    /* Update the body classes */
    useLayoutEffect(() => {
        document.body.className = body_classes ? body_classes.join(' ') : '';
    }, [body_classes]);

    const evalHashIfPossible = hash => {
        if (!loading) {
            const target = document.getElementById(hash);
            if (target) {
                return scroll({
                    by: target.getBoundingClientRect().top - hashScrollOffset,
                    behavior: 'smooth'
                });
            }
        }
        return executeAfter(() => evalHashIfPossible(hash), 100);
    };

    /* Evaluate */
    useLayoutEffect(() => {
        const hash = splitURL().hash;
        return hash ? evalHashIfPossible(hash) : () => {};
    }, [loading, hashScrollOffset, slug]);

    /* Update the WordPress Admin bar when the content changes */
    useLayoutEffect(() => {
        let customizeContainer = document.getElementById("wp-admin-bar-customize");
        if (customizeContainer) {
            let canonicalURL = encodeURIComponent(window.location.href);
            customizeContainer.firstChild.href = "/wp-admin/customize.php?url=" + canonicalURL;
        }
    }, [slug]);

    useLayoutEffect(() => {
        let containerListItem = document.getElementById("wp-admin-bar-edit");
        if (post_id) {
            if (!containerListItem) {
                let ab_top_menu = document.getElementById("wp-admin-bar-root-default");

                if (ab_top_menu) {
                    containerListItem = document.createElement("li");
                    containerListItem.id = "wp-admin-bar-edit";

                    let prevMenuItem = document.getElementById("wp-admin-bar-new-content");
                    if (prevMenuItem) {
                        ab_top_menu.insertBefore(containerListItem, prevMenuItem.nextSibling);
                    } else {
                        ab_top_menu.appendChild(containerListItem);
                    }
                }
            }
            if (containerListItem) {
                containerListItem.innerHTML = `<a class='ab-item' href='/wp-admin/post.php?post=${post_id}&action=edit'>${editText}</a>`;
            }
        } else if (containerListItem) {
            containerListItem.parentNode.removeChild(containerListItem);
        }
    }, [post_id, editText, isLoading]);

    const isLoading = loading || loadingReplacement;
    useLayoutEffect(() => {
        document.title = site_name + (webpage_title && !isLoading ? " | " + webpage_title : "");
    }, [site_name, webpage_title, isLoading]);

    useLayoutEffect(() => {
        let els = [];

        const el = s => {
            els = els.concat([...htmlToFragment(s).children]);
        };

        // TODO: Update existing elements

        if (faviconHref && faviconHref.length > 0) {
            el(`<link rel='shortcut icon' type="${faviconMime}" href="${faviconHref}" />`);
        }

        if (notFound) {
            el(`<meta name="robots" content="noindex" />`);
        }

        if (pwa.theme_color) {
            el(`<meta name="theme-color" content=${pwa.theme_color}/>`);
        }

        if (site_description) {
            el(`<meta name="description" content="${site_description}"/>`);
        }

        for (let e of els) document.head.append(e);

        return () => els.forEach(removeNode);
    }, [faviconHref, notFound, pwa.theme_color]);

    //
    // Listen to postMessage messages
    // from the WP Customizer & update redux state.
    //
    useEffect(() => {
        if (window.wp && wp.customize && watch_settings) {
            for (const [sectionName, section] of Object.entries(watch_settings)) {
                for (const [settingName, setting] of Object.entries(section)) {
                    const isAttachment = setting.is_attachment;
                    const isJSON = setting.is_json;

                    wp.customize(setting.key, x => x.bind(v => {
                        const set = newValue => {
                            let sectionDict = polyphony.getState()[sectionName];
                            polyphony.updateState({[sectionName]: {...sectionDict, [settingName]: newValue}});
                        };
                        if (isAttachment) {
                            getAttachment(v).then(set);
                        } else if (isJSON) {
                            set(JSON.parse(v));
                        } else {
                            set(v);
                        }
                    }));
                }
            }
        }
    }, [watch_settings]);

    return null;
});

export default Chrome;

